import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Loader2 } from '@parrot-software/design-system/icons';

import { isApiError } from '@subscriptions/application/errors';
import {
  SubscriptionStripeCheckout,
  WizardStepFooter,
  useWizardLock,
} from '@subscriptions/presentation/components/base';
import {
  resolveWizardOrigin,
  withWizardOrigin,
} from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import { PaymentSessionModes } from '@subscriptions/domain/types';
import type { PaymentSessionMode } from '@subscriptions/domain/types';
import { AnalyticsStatuses, TrackedPaymentMethods, WizardSteps } from '@subscriptions/presentation/constants';
import {
  useConfirmBlockPaymentSession,
  useSubscriptionAnalytics,
  useSubscriptionBlock,
} from '@subscriptions/presentation/hooks';

import type { ConfirmationRouteState } from './confirmation';

/**
 * Route state expected by the checkout step (pushed by the method-selection step).
 *
 * `clientSecret` mounts the embedded checkout and `providerSessionId` finalizes the
 * session — both are secrets and are never rendered or logged here (charter §5).
 */
export type CheckoutRouteState = {
  clientSecret: string;
  mode: PaymentSessionMode;
  providerSessionId: string;
};

type BlockCreationStepCheckoutProps = {
  blockUuid: string;
};

/**
 * Derives the successful confirmation variant from the Stripe session mode:
 * `NEW_PAYMENT_METHOD_AND_CHARGE` registered a card (`card-saved`), while every mode
 * that collected money — an overdue charge or a reactivation — lands on `payment-done`.
 */
const resolveSuccessVariant = (mode: PaymentSessionMode): 'card-saved' | 'payment-done' =>
  mode === PaymentSessionModes.NEW_PAYMENT_METHOD_AND_CHARGE ? 'card-saved' : 'payment-done';

/**
 * Card checkout step of the block creation wizard (step 4, CARD only).
 *
 * Reads the Stripe session (`clientSecret` / `mode` / `providerSessionId`) from
 * router state and mounts {@link SubscriptionStripeCheckout}. The only allowed backward
 * move (to fiscal data, step 3) is the wizard-level back wired for the checkout route:
 * on desktop it renders in this step's {@link WizardStepFooter} and on mobile in the
 * layout's top chrome bar, both fed by the wizard navigation context. When Stripe
 * fires `onComplete` the step finalizes via confirm-session and navigates to the
 * confirmation step (step 5) carrying the canonical {@link ConfirmationRouteState}:
 * the `card-saved` / `payment-done` variant on success (derived from `mode`), or the
 * `error` variant with the raw backend `errorCode` on failure (F3 maps it).
 *
 * `isUpdate` is recomputed from the block (`Boolean(block?.paymentMethod?.uuid)`),
 * mirroring the method-selection step — it is not threaded through route state.
 *
 * @param props - Component properties
 * @param props.blockUuid - Subscription block uuid for the in-flight wizard
 * @returns React element rendering the embedded checkout
 */
export const BlockCreationStepCheckout = ({ blockUuid }: BlockCreationStepCheckoutProps) => {
  const history = useHistory();
  const { search, state } = useLocation<CheckoutRouteState>();
  const { data: block } = useSubscriptionBlock(blockUuid);
  const { mutateAsync: confirmSession } = useConfirmBlockPaymentSession();
  const [isConfirming, setIsConfirming] = useState(false);
  const { trackPaymentFinished, trackPaymentStepViewed } = useSubscriptionAnalytics();
  const origin = resolveWizardOrigin(search);

  useEffect(() => {
    trackPaymentStepViewed({
      blockUuid,
      origin,
      paymentMethod: TrackedPaymentMethods.CARD,
      step: WizardSteps.CHECKOUT,
    });
  }, [blockUuid, origin, trackPaymentStepViewed]);

  useWizardLock(isConfirming);

  const goToConfirmation = useCallback(
    (confirmationState: ConfirmationRouteState) =>
      history.push(
        withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/confirmation`, search),
        confirmationState,
      ),
    [blockUuid, history, search],
  );

  const handleComplete = useCallback(async () => {
    setIsConfirming(true);
    const isUpdate = Boolean(block?.paymentMethod?.uuid);

    try {
      const paymentMethod = await confirmSession({
        blockUuid,
        isUpdate,
        mode: state.mode,
        providerSessionId: state.providerSessionId,
      });
      trackPaymentFinished({
        blockUuid,
        origin,
        paymentMethod: TrackedPaymentMethods.CARD,
        status: AnalyticsStatuses.SUCCESS,
      });
      goToConfirmation({
        chargedBlock: block?.stores ? { name: block.name, stores: block.stores } : undefined,
        mode: state.mode,
        paymentMethod,
        variant: resolveSuccessVariant(state.mode),
      });
    } catch (error) {
      trackPaymentFinished({
        blockUuid,
        origin,
        paymentMethod: TrackedPaymentMethods.CARD,
        status: AnalyticsStatuses.FAILED,
      });
      goToConfirmation({ errorCode: isApiError(error) ? error.code : undefined, variant: 'error' });
    }
  }, [
    block,
    blockUuid,
    confirmSession,
    goToConfirmation,
    origin,
    state.mode,
    state.providerSessionId,
    trackPaymentFinished,
  ]);

  if (isConfirming) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] flex-col items-center overflow-y-auto px-6 pb-12 pt-20 font-sans">
      <div className="flex w-full max-w-[640px] flex-col gap-6">
        <SubscriptionStripeCheckout clientSecret={state.clientSecret} onComplete={handleComplete} />
        <WizardStepFooter />
      </div>
    </div>
  );
};
